"use client";

import { useState } from "react";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "@/lib/firebase";
import Link from "next/link";
import { CheckCircle } from "lucide-react";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email.trim());
      setSent(true);
    } catch (err) {
      const code = (err as { code?: string })?.code;
      // Don't reveal whether the email exists — always show success
      if (code === "auth/user-not-found" || code === "auth/invalid-email") {
        setSent(true);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }

  if (sent) {
    return (
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <CheckCircle size={40} className="text-green-400" />
        </div>
        <h2 className="text-white font-semibold text-lg mb-2">Check your inbox</h2>
        <p className="text-zinc-400 text-sm mb-6">
          If an account exists for <span className="text-zinc-200">{email}</span>, you&apos;ll
          receive a password reset link shortly.
        </p>
        <Link href="/login" className="text-yellow-400 text-sm hover:underline">
          Back to Sign In
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8 text-center">
        <p className="text-zinc-400 text-sm">Enter your email to receive a reset link</p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm text-zinc-300 mb-1">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
            className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-2.5 text-white placeholder-zinc-500 focus:outline-none focus:border-yellow-400"
            placeholder="you@example.com"
          />
        </div>
        {error && <p className="text-red-400 text-sm">{error}</p>}
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-yellow-400 text-black font-semibold py-2.5 rounded-lg hover:bg-yellow-300 disabled:opacity-50 transition"
        >
          {loading ? "Sending…" : "Send Reset Link"}
        </button>
      </form>
      <p className="mt-6 text-center text-sm text-zinc-500">
        Remember your password?{" "}
        <Link href="/login" className="text-yellow-400 hover:underline">
          Sign in
        </Link>
      </p>
    </div>
  );
}
